import { FC, useCallback, useState } from 'react';
import { ScrollView, View } from 'react-native';
import { observer } from 'mobx-react';
import { useMutation } from '@apollo/client';

import { COLORS, ERROR_COLOR } from '../../../config/designSystem/Colors';
import { CREATE_MY_EXERCISE } from '../../../state/mutations/MyExercice';
import { ExerciseData } from './ContainExercise';
import { H4, ParagraphMBold, ParagraphMRegular } from '../../../config/designSystem/typography';
import { SPACING } from '../../../config/designSystem/Spacing';
import { SessionPresentationProps } from '../config';
import { TwoButtonsHorizontal } from '../../Button/TwoButtonsHorizontal';
import { convertFromUtf16 } from '../../../utils/others/emoji';
import { useBreakpointStyles } from '../../../hooks/useBreakpointStyles';
import { useOnMount } from '../../../hooks/useOnMount';

export interface PresentExerciseProps extends SessionPresentationProps<ExerciseData> {
  labels?: {
    REJECT?: string;
    ACCEPT?: string;
    ALREADY_ACCEPTED?: string;
    ALREADY_REJECTED?: string;
  }
  data: ExerciseData,
}

const _PresentExercise: FC<PresentExerciseProps> = ({
  labels,
  data,
}) => {
  const exercise = data.exercise;
  const pathId = data.pathId;
  const userId = data.userId;
  const language = data.language;
  const setDecided = data.setDecided;
  const setExerciseId = data.setExerciseId;
  const fetchMyExerciseById = data.fetchMyExerciseById;
  const addToDashboard = data.addToDashboard;

  const [choiceMade, setChoiceMade] = useState<boolean>(!!exercise?.myExerciceId || !!exercise?.completed);
  const [accepted, setAccepted] = useState<boolean>(!!exercise?.myExerciceId);

  useOnMount(() => {
    if (choiceMade) {
      setDecided(true);
    }
    if (exercise?.myExerciceId) {
      setExerciseId(exercise.myExerciceId);
    }
  });

  const [createMyExercise] = useMutation(CREATE_MY_EXERCISE);

  const styles = useBreakpointStyles({
    container: {
      flex: 1,
      height: '100%',
    },
    wrapper: {
      flex: 1,
      lg: {
        justifyContent: 'center',
      },
    },
    contentWrapper: {
      flex: 1,
      lg: {
        marginHorizontal: SPACING['sp-20'],
        marginBottom: SPACING['sp-12'],
      },
    },
    scrollContainer: {
      marginBottom: SPACING['sp-20'],
      flex: 1,
      lg: {
        marginBottom: 0,
      },
    },
    scrollContent: {
      marginHorizontal: SPACING['sp-20'],
      lg: {
        marginHorizontal: 0,
      },
    },
    mainContent: {
      marginBottom: SPACING['sp-32'],
    },
    stateAcceptance: {
      justifyContent: 'center',
      alignItems: 'center',
      padding: SPACING['sp-16'],
    },
  });

  const onAcceptedChallenge = useCallback(async () => {
    if (!exercise?._id || !userId) {
      console.error('Missing required data for exercise creation');
      return;
    }

    setAccepted(true);
    setChoiceMade(true);
    setDecided(true);

    try {
      const { data: mutationData } = await createMyExercise({
        variables: {
          input: {
            user: userId,
            exercice: exercise._id,
            language,
            path: pathId,
          },
        }
      });

      if (mutationData) {
        const myExerciceId = mutationData?.createMyExercice?._id;
        setExerciseId(myExerciceId);
        fetchMyExerciseById(myExerciceId);
        addToDashboard(mutationData.createMyExercice?._id);
      }
    } catch (error) {
      console.error('Error creating exercise:', error);
      // Revert state on error
      setAccepted(false);
      setChoiceMade(false);
    }
  }, [exercise._id, userId, setDecided, createMyExercise, language, pathId, setExerciseId, fetchMyExerciseById, addToDashboard]);

  const onDeclinedChallenge = useCallback(() => {
    setAccepted(false);
    setChoiceMade(true);
    setDecided(true);
  }, [setDecided]);

  const renderChoices = () => (
    <TwoButtonsHorizontal
      onPressLeft={onDeclinedChallenge}
      onPressRight={onAcceptedChallenge}
      labelLeft={labels?.REJECT || 'Reject'}
      labelRight={convertFromUtf16('1F680') + (labels?.ACCEPT || 'Accept')}
    />
  );

  const renderChoiceMade = () => {
    if (accepted) {
      return (
        <View style={styles.stateAcceptance}>
          <ParagraphMBold selectable={true} color={COLORS.state.success.primary}>
            {convertFromUtf16('1F389')}{labels?.ALREADY_ACCEPTED || 'Challenge accepted!'}
          </ParagraphMBold>
        </View>
      );
    } else {
      return (
        <View style={styles.stateAcceptance}>
          <ParagraphMBold selectable={true} color={ERROR_COLOR}>
            {convertFromUtf16('1F6AB')}{labels?.ALREADY_REJECTED || 'Challenge rejected'}
          </ParagraphMBold>
        </View>
      );
    }
  };

  const renderMainContent = () => (
    <View style={styles.mainContent}>
      <H4 align={true}>{exercise?.title}</H4>
      <View style={{ marginTop: SPACING['sp-16'] }}>
        <ParagraphMRegular selectable={true}>
          {exercise?.longDescription || exercise?.shortDescription}
        </ParagraphMRegular>
      </View>
    </View>
  );

  return (
    <View style={styles.container}>
      <View style={styles.wrapper}>
        <View style={styles.contentWrapper}>
          <ScrollView
            style={styles.scrollContainer}
            contentContainerStyle={styles.scrollContent}
          >
            {renderMainContent()}
          </ScrollView>
        </View>
        {!choiceMade ? renderChoices() : renderChoiceMade()}
      </View>
    </View>
  );
};

export const PresentExercise = observer(_PresentExercise);
